<template>
  <modal
    v-if="productLoaded"
    class="BasketView md:pin-r"
    :title="$t('titles.sizeAndQuantity')"
  >
    <base-row
      v-for="product in sizesWithSameColor"
      :key="product.articleNumber"
      class="justify-between p-2 items-center"
    >
      <span class="text-xs font-semibold">{{ product.size }}</span>
      <quantity
        :add="() => increaseAmount(product)"
        :change="newQuantity => changeProductAmount(product, newQuantity)"
        :dec="() => decreaseAmount(product)"
        :disabled="!product.active"
        :quantity="getQuantityByProduct(product)"
      />
    </base-row>
    <div slot="footer">
      <base-row class="flex-col px-2 pb-2">
        <div class="flex justify-between p-2">
          <span class="text-sm">
            {{ basketSize.toLocaleString() }} {{ $t('summary.selection') }}
          </span>
          <span class="text-sm font-semibold">
            {{ humanReadableArticlePrice }}
          </span>
        </div>
      </base-row>
      <base-row v-if="hasAdditionalCost" class="flex-col px-2 pb-2">
        <div class="flex flex-col p-2">
          <div class="text-sm">
            {{ $t('summary.additionalCosts') }}
          </div>
          <div class="flex justify-between pt-2 pl-2">
            <span class="text-sm"
              >{{ $t('customization.uploadBgRemoval') }} x{{
                uniqueBackgroundRemovals
              }}</span
            >
            <span class="text-sm font-semibold">{{
              totalBackgroundRemovalCost | currency
            }}</span>
          </div>
        </div>
      </base-row>
      <div class="p-2">
        <product-price :price="totalPrice" class="text-right pb-2" />
        <div
          v-if="shouldDisplayMaxValueExceededError"
          class="text-sm font-semibold exceed-error"
        >
          {{ $t('warning.maxGrossExceeded') }}
        </div>
        <base-button
          :disabled="
            basketSize === 0 || !hasPrice || maxGrossValueExceededError
          "
          icon="cart-icon"
          class="w-full"
          @click.native="sendBasket"
        >
          {{ $t('summary.addToBasket') }}
        </base-button>
        <shopware-basket-trigger
          ref="Trigger"
          :article-number="lastArticleNumber"
          :quantity="lastArticleQuantity"
          :composition="lastArticleComposition"
        />
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from '../components/Modal.vue';
import ProductPrice from '../components/ProductPrice.vue';
import Quantity from '../components/Quantity.vue';
import ShopwareBasketTrigger from '../components/ShopwareBasketTrigger.vue';
import axios from 'axios';
import Cookies from 'js-cookie';
import querystring from 'querystring';
import http from '../store/helpers/http-common';
import { MUTATION_RESET_BASKET } from '../store/modules/basket/mutation-types';
import { MUTATION_GET_PRICE } from '../store/modules/product/mutation-types';
import {
  MUTATION_REMOVE_FROM_BASKET,
  MUTATION_SET_QUANTITY_FOR_PRODUCT,
  MUTATION_ADD_TO_BASKET
} from '../store/modules/basket/mutation-types';
import currency from '../filters/currency';
import { ACTION_AWAIT_REQUEST } from '../store/action-types';
import {
  MAX_GROSS_VALUE,
  MAX_GROSS_VALUE_DISPLAY_MESSAGE,
  MAX_GROSS_VALUE_ENABLED,
  SHOPWARE_SHOP_ID
} from '../../utils/configure-environment';
import { TrackingMixin } from '../mixins/gtm-tracking';
import BaseRow from '../components/BaseRow';
import { BACKGROUND_REMOVAL_COST } from '../../utils/configure-environment';
import { GETTER_PRODUCT_CURRENT_PRODUCT } from '@/store/modules/product/getters';
import isMobile from '../../utils/is-mobile';
import {
  MUTATION_HIDE_NAVIGATION,
  MUTATION_SHOW_NAVIGATION
} from '../store/mutation-types';
import { useCompositionStore } from '@/store-pinia/composition';
import { mapState } from 'pinia';

export default {
  name: 'BasketView',
  components: {
    BaseRow,
    Modal,
    ProductPrice,
    Quantity,
    ShopwareBasketTrigger
  },
  filters: {
    currency
  },
  mixins: [TrackingMixin],
  data: () => ({
    lastArticleNumber: 'p',
    lastArticleQuantity: 1,
    lastArticleComposition: 'hi'
  }),
  computed: {
    productLoaded() {
      return this.$store.state.product.isLoaded;
    },
    sizesWithSameColor() {
      const currentColor = this.$store.getters['product/currentColor'];

      return this.$store.getters['product/availability'].filter(
        item => item.color === currentColor
      );
    },
    basketSize() {
      return this.$store.getters['basket/size'];
    },
    totalPrice() {
      return this.$store.getters.totalPrice;
    },
    maxGrossValueExceededError() {
      return MAX_GROSS_VALUE_ENABLED && this.totalPrice > MAX_GROSS_VALUE;
    },
    shouldDisplayMaxValueExceededError() {
      return this.maxGrossValueExceededError && MAX_GROSS_VALUE_DISPLAY_MESSAGE;
    },
    hasPrice() {
      return this.$store.getters['product/price'] !== undefined;
    },
    hasAdditionalCost() {
      return this.uniqueBackgroundRemovals > 0;
    },
    humanReadableArticlePrice() {
      const articlePrice = this.$store.getters['product/price'];

      return this.hasPrice
        ? `${this.$t('summary.articlePrice')}: ${this.$options.filters.currency(
            articlePrice
          )}`
        : this.$t('summary.loadingArticlePrice');
    },
    totalBackgroundRemovalCost() {
      return this.uniqueBackgroundRemovals * BACKGROUND_REMOVAL_COST;
    },
    uniqueBackgroundRemovals() {
      return this.$store.getters['composition/uniqueBackgroundRemovals'];
    },
    ...mapState(useCompositionStore, ['composition'])
  },
  mounted() {
    this.resetBasket();

    // Add the current selection of the product in Creator in Basket amount if product is marked active
    if (this.$store.getters[GETTER_PRODUCT_CURRENT_PRODUCT].active) {
      this.increaseAmount(this.$store.getters[GETTER_PRODUCT_CURRENT_PRODUCT]);
    }

    // Hide mobile navigation
    this.hideNavigationIfMobile();

    this.$store.dispatch(`product/${MUTATION_GET_PRICE}`, this.composition);
  },
  destroyed() {
    this.$store.commit(`basket/${MUTATION_RESET_BASKET}`);
    this.showNavigationIfMobile();
  },
  methods: {
    hideNavigationIfMobile() {
      if (isMobile()) {
        this.$store.commit(MUTATION_HIDE_NAVIGATION);
      }
    },
    showNavigationIfMobile() {
      if (isMobile()) {
        this.$store.commit(MUTATION_SHOW_NAVIGATION);
      }
    },
    resetBasket() {
      this.$store.commit(`basket/${MUTATION_RESET_BASKET}`);
    },
    changeProductAmount(product, newQuantity) {
      this.$store.commit(`basket/${MUTATION_SET_QUANTITY_FOR_PRODUCT}`, [
        product,
        newQuantity || 0
      ]);
    },
    getQuantityByProduct(product) {
      return this.$store.getters['basket/quantityByProduct'](product);
    },
    increaseAmount(product) {
      this.$store.commit(`basket/${MUTATION_ADD_TO_BASKET}`, product);
    },
    decreaseAmount(product) {
      this.$store.commit(`basket/${MUTATION_REMOVE_FROM_BASKET}`, product);
    },
    async sendBasket() {
      const requests = [];
      const articles = [];
      const encodedComposition = window.btoa(
        encodeURIComponent(JSON.stringify(this.composition))
      );

      const csrfToken = Cookies.get('__csrf_token-' + SHOPWARE_SHOP_ID);

      const products = this.$store.getters['basket/items'];
      let lastProduct;

      products.forEach(product => {
        if (undefined === lastProduct) {
          lastProduct = product;
          return;
        }

        requests.push(
          http.post(
            `${this.$store.state.shopwareBaseUrl}/checkout/ajaxAddArticleCart`,
            querystring.stringify({
              sAdd: product.articleNumber,
              sQuantity: product.quantity,
              sCompositionHash: encodedComposition,
              isXHR: 1
            }),
            {
              headers: {
                'X-CSRF-Token': csrfToken,
                'Content-Type':
                  'application/x-www-form-urlencoded; charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest'
              }
            }
          )
        );

        articles.push({
          articleNumber: product.articleNumber
        });
      });

      articles.push({
        articleNumber: lastProduct.articleNumber
      });

      this.$store.dispatch(ACTION_AWAIT_REQUEST, async () => {
        await axios.all(requests);

        Cookies.set('addedArticles', articles, { secure: true });

        this.lastArticleNumber = lastProduct.articleNumber;
        this.lastArticleQuantity = lastProduct.quantity;
        this.lastArticleComposition = encodedComposition;

        this.$refs.Trigger.trigger();

        this.$store.commit(`basket/${MUTATION_RESET_BASKET}`);
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.BasketView {
  @apply bg-white;
  left: auto;
  .exceed-error {
    border: 2px solid red;
    background-color: #fee;
    padding: 1rem;
    margin: 0.5rem 0;
    color: red;
  }
}

@screen md {
  .BasketView {
    @apply m-0;
    width: 460px;
  }
}
</style>
